import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  SearchServiceCentersDictionaryRequest,
  ServiceCenterDictionaryClient,
} from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { catchError, exhaustMap, map, Observable, of, startWith, Subject, tap } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { LoadingState } from '../../types/loading.type';
import { TranslateService } from '@ngx-translate/core';

interface IServiceCentersRequest extends SearchServiceCentersDictionaryRequest {
  skip: number;
  take: number;
  includeTotalCount: boolean;
}

@Injectable()
export class ServiceCenterDictionaryService {
  private readonly serviceCenterDictionaryClient = inject(ServiceCenterDictionaryClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly loadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );

  private readonly skipCount = 10 as const;

  private request: IServiceCentersRequest = {
    skip: 0,
    take: 10,
    includeTotalCount: true,
  };

  private readonly origin$: Subject<void> = new Subject<void>();

  public readonly source$ = this.origin$.pipe(
    startWith(void 0),
    tap((): void => {
      this.loadingState.set(LoadingState.LOADING);
    }),
    exhaustMap(() =>
      this.serviceCenterDictionaryClient.v1ServiceCenterDictionarySearchPost(this.request).pipe(
        tap(({ totalCount }): void => {
          this.totalCount.set(totalCount ?? 0);
        }),
        map(({ items }): IDictionaryItem<string>[] => items ?? []),
        catchError((err: unknown): Observable<IDictionaryItem<string>[]> => {
          this.request = {
            ...this.request,
            skip: Math.max(this.request.skip - this.skipCount, 0),
          };

          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.selector.toast.service-centers-fetching-error',
            ),
          });

          console.error('Error fetching service center dictionary items:', err);

          return of([]);
        }),
      ),
    ),
    tap((): void => {
      this.loadingState.set(LoadingState.READY);
    }),
  );

  public updateKeyword(keyword: string): void {
    this.request = {
      ...this.request,
      skip: 0,
      keyword,
    };
  }

  public loadMore(): void {
    if (this.loadingState() === LoadingState.LOADING) return;
    if (this.request.skip + this.skipCount >= this.totalCount()) return;

    this.request = {
      ...this.request,
      skip: this.request.skip + this.skipCount,
    };

    this.origin$.next();
  }
}
